/**
 * MultiSelectFilter Component
 *
 * A filter dropdown that allows selecting multiple options.
 * Uses Popover with checkboxes for multi-selection.
 */

import * as React from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Badge } from './Badge';
import { Checkbox } from './Checkbox';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectFilterProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  maxDisplayed?: number;
}

export function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  placeholder = 'All',
  className,
  maxDisplayed = 2,
}: MultiSelectFilterProps) {
  const [open, setOpen] = React.useState(false);

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const selectAll = () => {
    onChange(options.map(o => o.value));
  };

  const selectedLabels = selected
    .map(v => options.find(o => o.value === v)?.label)
    .filter((l): l is string => !!l);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label className="text-sm font-medium text-muted-foreground">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between min-w-[180px] h-auto min-h-10 py-2"
          >
            <div className="flex flex-wrap gap-1 flex-1 text-left">
              {selected.length === 0 ? (
                <span className="text-muted-foreground">{placeholder}</span>
              ) : selected.length <= maxDisplayed ? (
                selectedLabels.map(label => (
                  <Badge key={label} variant="secondary" className="text-xs">
                    {label}
                  </Badge>
                ))
              ) : (
                <Badge variant="secondary" className="text-xs">
                  {selected.length} selected
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1 ml-2">
              {selected.length > 0 && (
                <X
                  className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer"
                  onClick={clearAll}
                />
              )}
              <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0" align="start">
          <div className="p-2 border-b border-border">
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 h-8"
                onClick={selectAll}
              >
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 h-8"
                onClick={() => onChange([])}
              >
                Clear
              </Button>
            </div>
          </div>
          <div className="max-h-[300px] overflow-y-auto p-2">
            {options.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-4">
                No options available
              </div>
            ) : (
              <div className="space-y-1">
                {options.map(option => (
                  <div
                    key={option.value}
                    className={cn(
                      'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-muted',
                      selected.includes(option.value) && 'bg-muted'
                    )}
                    onClick={() => toggleOption(option.value)}
                  >
                    <Checkbox
                      checked={selected.includes(option.value)}
                      onCheckedChange={() => toggleOption(option.value)}
                      className="pointer-events-none"
                    />
                    <span className="text-sm flex-1 truncate">{option.label}</span>
                    {selected.includes(option.value) && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
